import React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  Select,
  TextField,
  NumberTextField,
  useValidateOnMount,
} from 'swipeclock-frontend';
import PropTypes from 'prop-types';

const GridBox = styled((props) => <Box {...props} />)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  justifyContent: 'center',
  alignItems: 'flex-start',
  width: '100%',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

/**
 * Helper hook that renders the correct input component based on prompt type
 */
function useFormComponentRenderer(formBag) {
  const { getError, handleBlur, handleSelect, handleChange, validationRef } =
    formBag;

  const renderComponent = (prompt) => {
    const {
      id,
      type,
      label,
      required,
      options,
      maxLength: max,
    } = prompt;

    switch (type) {
      case 'dropdown':
        return (
          <Select
            key={id}
            id={id}
            label={label}
            onBlur={handleBlur(id)}
            onChange={handleSelect(id)}
            options={options}
            required={required}
            validationRef={validationRef}
            hint={label}
            error={getError(id)}
            defaultValue=""
          />
        );

      case 'decimal':
        return (
          <NumberTextField
            key={id}
            id={id}
            label={label}
            formatType="currency"
            onBlur={handleBlur(id)}
            onChange={handleChange(id)}
            placeholder={`Enter: ${label}`}
            required={required}
            validationRef={validationRef}
            hint={label}
            error={getError(id)}
            maxLength={max}
          />
        );

      case 'integer':
        return (
          <NumberTextField
            key={id}
            id={id}
            label={label}
            formatType="integer"
            onBlur={handleBlur(id)}
            onChange={handleChange(id)}
            placeholder={`Enter: ${label}`}
            required={required}
            validationRef={validationRef}
            hint={label}
            error={getError(id)}
            maxLength={max}
          />
        );

      case 'numeric': // Float
        return (
          <NumberTextField
            key={id}
            id={id}
            label={label}
            formatType="float"
            onBlur={handleBlur(id)}
            onChange={handleChange(id)}
            placeholder={`Enter: ${label}`}
            required={required}
            validationRef={validationRef}
            hint={label}
            error={getError(id)}
            maxLength={max}
          />
        );

      case 'alphanumeric':
      default:
        return (
          <TextField
            key={id}
            id={id}
            label={label}
            onBlur={handleBlur(id)}
            onChange={handleChange(id)}
            placeholder={`Enter: ${label}`}
            required={required}
            type="text"
            validationRef={validationRef}
            hint={label}
            error={getError(id)}
            maxLength={max}
          />
        );
    }
  };

  return { renderComponent };
}

function PromptFormFields({ formBag, prompts }) {
  const {
    getError = () => null,
    handleBlur = () => () => {},
    handleSelect = () => () => {},
    handleChange = () => () => {},
    validationRef = { current: {} },
    isValid = () => true,
    setDisabled = () => {},
  } = formBag;

  const { renderComponent } = useFormComponentRenderer({
    getError,
    handleBlur,
    handleSelect,
    handleChange,
    validationRef,
  });

  useValidateOnMount(() => setDisabled(!isValid()), { skip: false });

  return <GridBox>{prompts.map((prompt) => renderComponent(prompt))}</GridBox>;
}

PromptFormFields.propTypes = {
  formBag: PropTypes.shape({
    getError: PropTypes.func,
    handleBlur: PropTypes.func,
    handleSelect: PropTypes.func,
    handleChange: PropTypes.func,
    validationRef: PropTypes.object,
    isValid: PropTypes.func,
    setDisabled: PropTypes.func,
  }).isRequired,
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.array,
      min: PropTypes.number,
      max: PropTypes.number,
    })
  ).isRequired,
};

export default function FormComponent({ formBag = {}, prompts = [] }) {
  if (!prompts?.length) {
    return (
      <Box sx={{ py: 2 }}>
        <p>No additional information needed.</p>
      </Box>
    );
  }

  return <PromptFormFields formBag={formBag} prompts={prompts} />;
}

FormComponent.propTypes = {
  formBag: PropTypes.shape({
    getError: PropTypes.func,
    handleBlur: PropTypes.func,
    handleSelect: PropTypes.func,
    handleChange: PropTypes.func,
    validationRef: PropTypes.object,
    validate: PropTypes.func,
  }),
  prompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.array,
      min: PropTypes.number,
      max: PropTypes.number,
    })
  ),
};
